import CmsService from "../component/services/CmsService";
import {
  getAttributionSource,
  getMarketingAttributionData,
  isPaidAdsUser,
} from "../config/MarketingAttribution";

let providerFlagsCache = null;

const normalizeProviderFlag = (value, defaultValue = true) => {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();

    if (["1", "true", "yes", "on"].includes(normalizedValue)) {
      return true;
    }

    if (["0", "false", "no", "off"].includes(normalizedValue)) {
      return false;
    }
  }

  return Boolean(value);
};

export const setProviderFlags = (flags = {}) => {
  providerFlagsCache = {
    ADS_META_ENABLED: normalizeProviderFlag(flags.ADS_META_ENABLED, true),
    ADS_GOOGLE_ENABLED: normalizeProviderFlag(flags.ADS_GOOGLE_ENABLED, true),
    SIMPLIFI_ENABLED: normalizeProviderFlag(flags.SIMPLIFI_ENABLED, true),
  };
};

export const getProviderFlags = () => providerFlagsCache;

const isProviderEnabled = (provider) => {
  const normalizedProvider =
    typeof provider === "string" ? provider.trim().toLowerCase() : "";

  if (!normalizedProvider) {
    return false;
  }

  if (!providerFlagsCache) {
    return true;
  }

  const providerKeyMap = {
    meta: "ADS_META_ENABLED",
    google: "ADS_GOOGLE_ENABLED",
    simplifi: "SIMPLIFI_ENABLED",
  };

  const providerKey = providerKeyMap[normalizedProvider];
  if (!providerKey) {
    return false;
  }

  return normalizeProviderFlag(providerFlagsCache[providerKey], true);
};
const META_BROWSER_ONCE_PREFIX = "meta_browser_once:";
const META_BROWSER_RETRY_DELAY_MS = 300;
const META_BROWSER_MAX_RETRIES = 5;

const hasTrackedMetaBrowserEvent = (eventId) => {
  if (typeof window === "undefined" || !eventId) {
    return false;
  }

  return window.sessionStorage.getItem(`${META_BROWSER_ONCE_PREFIX}${eventId}`) === "1";
};

const markTrackedMetaBrowserEvent = (eventId) => {
  if (typeof window === "undefined" || !eventId) {
    return;
  }

  window.sessionStorage.setItem(`${META_BROWSER_ONCE_PREFIX}${eventId}`, "1");
};

const isMetaPixelReady = () =>
  typeof window !== "undefined" && typeof window.fbq === "function";

const sendMetaBrowserEvent = (
  standardEventName,
  params = {},
  eventId = "",
  retriesRemaining = META_BROWSER_MAX_RETRIES
) => {
  if (typeof window === "undefined") {
    return;
  }

  if (eventId && hasTrackedMetaBrowserEvent(eventId)) {
    return;
  }

  if (!isMetaPixelReady()) {
    if (retriesRemaining <= 0) {
      return;
    }

    window.setTimeout(() => {
      sendMetaBrowserEvent(
        standardEventName,
        params,
        eventId,
        retriesRemaining - 1
      );
    }, META_BROWSER_RETRY_DELAY_MS);
    return;
  }

  if (eventId) {
    window.fbq("track", standardEventName, params || {}, { eventID: eventId });
    markTrackedMetaBrowserEvent(eventId);
    return;
  }

  window.fbq("track", standardEventName, params || {});
};
const sanitizeEventIdPart = (value) =>
  String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9:_-]/g, "_");

const getSharedEventId = (eventName, payload = {}, marketingData = {}) => {
  const provider =
    typeof marketingData.provider === "string"
      ? marketingData.provider.trim().toLowerCase()
      : "";
  const reservationSlug =
    typeof payload?.reservation_slug === "string"
      ? payload.reservation_slug.trim()
      : "";
  const listingSlug =
    typeof payload?.listing_slug === "string" ? payload.listing_slug.trim() : "";
  const pagePath =
    typeof payload?.page_path === "string"
      ? payload.page_path.trim()
      : typeof window !== "undefined"
        ? window.location.pathname || ""
        : "";
  const checkIn =
    typeof payload?.checkin_date === "string" ? payload.checkin_date.trim() : "";
  const checkOut =
    typeof payload?.checkout_date === "string"
      ? payload.checkout_date.trim()
      : "";
  const landingUrl =
    typeof marketingData.landing_url === "string"
      ? marketingData.landing_url.trim()
      : "";

  if (eventName === "lead_visit" || eventName === "lead_created") {
    return [
      "ads",
      sanitizeEventIdPart(provider || "other"),
      sanitizeEventIdPart(eventName),
      sanitizeEventIdPart(landingUrl || pagePath || "visit"),
    ].join(":");
  }

  if (eventName === "booking_initiated") {
    return [
      "ads",
      sanitizeEventIdPart(provider || "other"),
      "booking_initiated",
      sanitizeEventIdPart(reservationSlug || listingSlug || pagePath || "booking"),
      sanitizeEventIdPart(checkIn || "checkin"),
      sanitizeEventIdPart(checkOut || "checkout"),
    ].join(":");
  }

  if (eventName === "reservation_completed") {
    return [
      "ads",
      sanitizeEventIdPart(provider || "other"),
      "reservation_completed",
      sanitizeEventIdPart(reservationSlug || listingSlug || pagePath || "reservation"),
    ].join(":");
  }

  return "";
};

const trackMetaEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const eventId = payload?.event_id || "";

  if (eventName === "lead_visit" || eventName === "lead_created") {
    sendMetaBrowserEvent("Lead", {}, eventId);
    return;
  }

  if (eventName === "booking_initiated") {
    sendMetaBrowserEvent("InitiateCheckout", {}, eventId);
    return;
  }

  if (eventName === "reservation_completed") {
    const rawValue =
      payload?.value ??
      payload?.total_amount ??
      payload?.total_payment ??
      payload?.payment ??
      0;
    const parsedValue = parseFloat(rawValue);
    const value = Number.isFinite(parsedValue) ? parsedValue : 0;
    const currency = payload?.currency || "USD";
    sendMetaBrowserEvent("Purchase", {
      value,
      currency,
    }, eventId);
  }
};

const getGoogleSendTo = (eventName) => {
  const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
  if (!conversionId) {
    return "";
  }

  const labelMap = {
    lead_visit: process.env.REACT_APP_GOOGLE_ADS_LEAD_LABEL,
    lead_created: process.env.REACT_APP_GOOGLE_ADS_LEAD_LABEL,
    booking_initiated: process.env.REACT_APP_GOOGLE_ADS_BOOKING_INITIATED_LABEL,
    reservation_completed: process.env.REACT_APP_GOOGLE_ADS_BOOKING_COMPLETED_LABEL,
  };

  const label = labelMap[eventName];
  if (!label) {
    return "";
  }

  return `${conversionId}/${label}`;
};

const trackGoogleAdsEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  const sendTo = getGoogleSendTo(eventName);
  if (!sendTo) {
    return;
  }

  const conversionPayload = {
    send_to: sendTo,
  };

  if (eventName === "reservation_completed") {
    const rawValue =
      payload?.value ??
      payload?.total_amount ??
      payload?.total_payment ??
      payload?.payment ??
      0;
    const parsedValue = parseFloat(rawValue);
    conversionPayload.value = Number.isFinite(parsedValue) ? parsedValue : 0;
    conversionPayload.currency = payload?.currency || "USD";
    if (payload?.reservation_slug) {
      conversionPayload.transaction_id = payload.reservation_slug;
    }
  }

  window.gtag("event", "conversion", conversionPayload);
};

const getEventDedupeKey = (eventName, payload = {}, marketingData = {}) => {
  if (typeof window === "undefined") {
    return "";
  }

  if (eventName === "lead_visit" || eventName === "lead_created") {
    const provider =
      typeof marketingData.provider === "string"
        ? marketingData.provider.trim().toLowerCase()
        : "";
    const landingUrl =
      typeof marketingData.landing_url === "string"
        ? marketingData.landing_url.trim()
        : window.location.href || "";

    if (!provider || !landingUrl) {
      return "";
    }

    return `ads_event:${eventName}:${provider}:${landingUrl}`;
  }

  return "";
};

const hasTrackedEventInSession = (dedupeKey) => {
  if (typeof window === "undefined" || !dedupeKey) {
    return false;
  }

  return window.sessionStorage.getItem(dedupeKey) === "1";
};

const markTrackedEventInSession = (dedupeKey) => {
  if (typeof window === "undefined" || !dedupeKey) {
    return;
  }

  window.sessionStorage.setItem(dedupeKey, "1");
};

const getCookieValue = (name) => {
  if (typeof document === "undefined" || !document.cookie) {
    return "";
  }

  const cookieName = `${name}=`;
  const cookies = document.cookie.split(";");
  for (let index = 0; index < cookies.length; index += 1) {
    const cookie = cookies[index].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return decodeURIComponent(cookie.substring(cookieName.length));
    }
  }

  return "";
};

export const pushDataLayerEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const marketingData = getMarketingAttributionData() || {};
  const eventId =
    payload?.event_id || getSharedEventId(eventName, payload, marketingData);
  const sfiClickId =
    typeof marketingData.sfi_click_id === "string"
      ? marketingData.sfi_click_id.trim()
      : "";

  const basePayload = {
    event: eventName,
    page_path: window.location.pathname || "",
    page_url: window.location.href || "",
    property_slug: typeof global !== "undefined" && global.property_slug
      ? global.property_slug
      : "",
    provider:
      typeof marketingData.provider === "string"
        ? marketingData.provider.trim().toLowerCase()
        : "",
    gclid:
      typeof marketingData.gclid === "string"
        ? marketingData.gclid.trim()
        : "",
    fbclid:
      typeof marketingData.fbclid === "string"
        ? marketingData.fbclid.trim()
        : "",
    sfi_click_id: sfiClickId,
    event_id: eventId,
  };

  const nextPayload =
    payload && typeof payload === "object" ? { ...basePayload, ...payload } : basePayload;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(nextPayload);
};

export const postAdsEvent = async (eventName, payload = {}) => {
  const marketingData = getMarketingAttributionData() || {};
  const provider = getAttributionSource();
  if (provider !== "meta" && provider !== "google") {
    if (process.env.REACT_APP_CUSTOM_NODE_ENV === "staging") {
      console.info("[Ads] skipping non-paid attribution", {
        eventName,
        provider,
      });
    }
    return;
  }
  if (provider && !isProviderEnabled(provider)) {
    if (process.env.REACT_APP_CUSTOM_NODE_ENV === "staging") {
      console.info("[Ads] provider disabled, skipping post", {
        eventName,
        provider,
        flags: providerFlagsCache,
      });
    }
    return;
  }
  const postData =
    payload && typeof payload === "object" ? { ...payload } : {};
  const eventId = postData.event_id || getSharedEventId(eventName, postData, marketingData);

  postData.event_type = eventName;
  postData.event_id = eventId;
  postData.property_slug =
    postData.property_slug ||
    (typeof global !== "undefined" && global.property_slug
      ? global.property_slug
      : "");
  postData.page_path = postData.page_path || window.location.pathname || "";
  postData.page_url = postData.page_url || window.location.href || "";
  postData.marketing = postData.marketing || marketingData;
  if (postData.marketing && typeof postData.marketing === "object") {
    postData.marketing = {
      ...postData.marketing,
      fbp:
        postData.marketing.fbp ||
        getCookieValue("_fbp") ||
        "",
      fbc:
        postData.marketing.fbc ||
        getCookieValue("_fbc") ||
        "",
    };
  }
  if (provider) {
    postData.provider = provider;
  }

  const inputData = {
    endPoint: "store-ads-event",
    addditionalHeaderData: {},
    postData: postData,
  };

  try {
    if (process.env.REACT_APP_CUSTOM_NODE_ENV === "staging") {
      console.info("[Ads] posting event", {
        eventName,
        provider,
        postData,
      });
    }
    await CmsService.postCMSCall(inputData);
  } catch (error) {
    if (process.env.REACT_APP_CUSTOM_NODE_ENV === "staging") {
      console.warn("[Ads] post failed", {
        eventName,
        provider,
        error,
      });
    }
    // No-op: ads tracking should never block the user flow
  }
};

export const trackAdsBrowserEventOnly = (eventName, payload = {}) => {
  const marketingData = getMarketingAttributionData() || {};
  const provider = getAttributionSource();
  const nextPayload =
    payload && typeof payload === "object" ? { ...payload } : {};
  nextPayload.event_id =
    nextPayload.event_id || getSharedEventId(eventName, nextPayload, marketingData);

  if (!isPaidAdsUser()) {
    return;
  }
  if (!isProviderEnabled(provider)) {
    return;
  }

  if (provider === "meta") {
    trackMetaEvent(eventName, nextPayload);
  }
  if (provider === "google") {
    trackGoogleAdsEvent(eventName, nextPayload);
  }
};
export const trackAdsEvent = (eventName, payload = {}) => {
  const marketingData = getMarketingAttributionData() || {};
  const provider = getAttributionSource();
  const nextPayload =
    payload && typeof payload === "object" ? { ...payload } : {};
  nextPayload.event_id =
    nextPayload.event_id || getSharedEventId(eventName, nextPayload, marketingData);
  const dedupeKey = getEventDedupeKey(eventName, nextPayload, marketingData);
  if (process.env.REACT_APP_CUSTOM_NODE_ENV === "staging") {
    console.info("[Ads] provider resolution", {
      eventName,
      provider,
      utm_source: marketingData.utm_source || "",
      utm_medium: marketingData.utm_medium || "",
      utm_campaign: marketingData.utm_campaign || "",
      gclid: marketingData.gclid || "",
      fbclid: marketingData.fbclid || "",
    });
  }
  if (!isPaidAdsUser()) {
    return;
  }
  if (dedupeKey && hasTrackedEventInSession(dedupeKey)) {
    return;
  }
  if (!isProviderEnabled(provider)) {
    return;
  }
  if (provider === "meta") {
    trackMetaEvent(eventName, nextPayload);
  }
  if (provider === "google") {
    trackGoogleAdsEvent(eventName, nextPayload);
  }
  pushDataLayerEvent(eventName, nextPayload);
  postAdsEvent(eventName, nextPayload);
  markTrackedEventInSession(dedupeKey);
};







